import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  BookOpen,
  Calendar,
  HelpCircle,
  Home,
  LayoutDashboard,
  Layers,
  MessageCircle,
  Settings,
  User,
  X,
} from 'lucide-react';
import { AppPhase } from '../../types';
import {
  isCommunicatePhase,
  isGradeWorkspacePhase,
  isHomeHubPhase,
  isOptionsPhase,
  isPlanPhase,
  isSchedulePhase,
  isTeachPhase,
} from '../../lib/appShellPhaseGroups';

export type MobileShellDrawerProps = {
  open: boolean;
  onClose: () => void;
  phase: AppPhase;
  isSignedIn: boolean;
  isOnline: boolean;
  setPhase: (p: AppPhase) => void;
  bumpNavUsage: (key: 'plan' | 'grade' | 'teach' | 'communicate') => void;
  recordShortcutUse: (
    key: 'plan' | 'grade' | 'teach' | 'schedule' | 'communicate' | 'options'
  ) => void;
};

type NavItem = {
  id: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  active: boolean;
  disabled?: boolean;
  disabledTitleKey?: string;
  onSelect: () => void;
  activeBarClass: string;
  accentClass: string;
};

export function MobileShellDrawer({
  open,
  onClose,
  phase,
  isSignedIn,
  isOnline,
  setPhase,
  bumpNavUsage,
  recordShortcutUse,
}: MobileShellDrawerProps): React.ReactElement | null {
  const { t } = useTranslation();

  const go = useCallback(
    (p: AppPhase) => {
      setPhase(p);
      onClose();
    },
    [onClose, setPhase]
  );

  const items: NavItem[] = [
    {
      id: 'home',
      labelKey: 'nav.home',
      icon: Home,
      active: isHomeHubPhase(phase),
      onSelect: () => go(AppPhase.AUTHENTICATION),
      activeBarClass: 'border-l-blue-500 dark:border-l-blue-400',
      accentClass: 'text-dg-accent-home dark:text-blue-400',
    },
    {
      id: 'plan',
      labelKey: 'nav.plan',
      icon: BookOpen,
      active: isPlanPhase(phase),
      disabled: !isSignedIn,
      onSelect: () => {
        if (!isSignedIn) return;
        recordShortcutUse('plan');
        go(AppPhase.PLAN);
      },
      activeBarClass: 'border-l-dg-accent-plan dark:border-l-violet-400',
      accentClass: 'text-dg-accent-plan dark:text-violet-400',
    },
    {
      id: 'grade',
      labelKey: 'nav.grade',
      icon: LayoutDashboard,
      active: isGradeWorkspacePhase(phase),
      disabled: !isSignedIn,
      onSelect: () => isSignedIn && go(AppPhase.DASHBOARD),
      activeBarClass: 'border-l-dg-accent-grade dark:border-l-amber-400',
      accentClass: 'text-dg-accent-grade dark:text-amber-400',
    },
    {
      id: 'teach',
      labelKey: 'nav.teach',
      icon: Layers,
      active: isTeachPhase(phase),
      disabled: !isSignedIn || !isOnline,
      disabledTitleKey: !isSignedIn ? undefined : !isOnline ? 'nav.teachOfflineHint' : undefined,
      onSelect: () => {
        if (!isSignedIn || !isOnline) return;
        bumpNavUsage('teach');
        go(AppPhase.CLASSROOM);
      },
      activeBarClass: 'border-l-dg-accent-teach dark:border-l-teal-400',
      accentClass: 'text-dg-accent-teach dark:text-teal-400',
    },
    {
      id: 'schedule',
      labelKey: 'nav.schedule',
      icon: Calendar,
      active: isSchedulePhase(phase),
      disabled: !isSignedIn,
      onSelect: () => isSignedIn && go(AppPhase.SCHEDULE),
      activeBarClass: 'border-l-dg-accent-schedule dark:border-l-emerald-400',
      accentClass: 'text-dg-accent-schedule dark:text-emerald-400',
    },
    {
      id: 'communicate',
      labelKey: 'nav.communicate',
      icon: MessageCircle,
      active: isCommunicatePhase(phase),
      disabled: !isSignedIn || !isOnline,
      disabledTitleKey: !isSignedIn
        ? 'nav.communicateSignIn'
        : !isOnline
          ? 'nav.communicateNeedsInternet'
          : undefined,
      onSelect: () => {
        if (!isSignedIn || !isOnline) return;
        bumpNavUsage('communicate');
        recordShortcutUse('communicate');
        go(AppPhase.RECORDS);
      },
      activeBarClass: 'border-l-dg-accent-communicate dark:border-l-rose-400',
      accentClass: 'text-dg-accent-communicate dark:text-rose-400',
    },
    {
      id: 'options',
      labelKey: 'nav.options',
      icon: Settings,
      active: isOptionsPhase(phase),
      disabled: !isSignedIn,
      onSelect: () => isSignedIn && go(AppPhase.OPTIONS),
      activeBarClass: 'border-l-dg-accent-options dark:border-l-slate-400',
      accentClass: 'text-dg-accent-options dark:text-slate-300',
    },
  ];

  if (!open) return null;

  return (
    <div className="lg:hidden" role="presentation">
      <button
        type="button"
        className="fixed inset-0 z-[95] bg-slate-950/50 backdrop-blur-[2px]"
        aria-label={t('shell.closeMenu')}
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t('shell.mobileNavAria')}
        className="fixed inset-y-0 left-0 z-[96] flex w-[min(18rem,88vw)] flex-col border-r border-dg-border bg-dg-bg-surface shadow-2xl dark:border-slate-800 dark:bg-slate-950"
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-dg-border-subtle px-3 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] dark:border-slate-800">
          <img
            src="/donegrading-logo.png"
            alt="DoneGrading"
            decoding="async"
            className="h-9 w-9 shrink-0 rounded-full object-cover"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-slate-900 dark:text-slate-100">
              DoneGrading
            </p>
            <p className="truncate text-[10px] font-semibold text-slate-500 dark:text-slate-400">
              {t('shell.workspace')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-dg-control border border-dg-border p-2 text-dg-fg-secondary transition-colors hover:bg-dg-interaction-hover dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label={t('shell.closeMenu')}
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2 py-2 custom-scrollbar">
          {items.map((item) => {
            const Icon = item.icon;
            const title =
              item.id === 'plan' && isSignedIn && !isOnline
                ? t('nav.planOfflineHint')
                : item.disabled && item.disabledTitleKey
                  ? t(item.disabledTitleKey)
                  : undefined;
            return (
              <button
                key={item.id}
                type="button"
                disabled={item.disabled}
                title={title}
                onClick={item.onSelect}
                aria-current={item.active ? 'page' : undefined}
                className={`relative flex min-h-[var(--dg-touch-min)] items-center gap-3 rounded-dg-control py-2.5 pl-3 pr-3 text-left transition-colors ${
                  item.active
                    ? `dg-nav-rail-active ${item.activeBarClass}`
                    : item.disabled
                      ? 'cursor-not-allowed border-l-4 border-l-transparent text-dg-fg-disabled opacity-60 dark:text-slate-600'
                      : 'border-l-4 border-l-transparent text-dg-fg-secondary hover:bg-dg-interaction-hover dark:text-slate-200 dark:hover:bg-slate-800/80'
                }`}
              >
                <Icon
                  className={`h-5 w-5 shrink-0 ${item.active ? item.accentClass : ''}`}
                  aria-hidden
                />
                <span
                  className={`truncate text-dg-body ${item.active ? 'font-semibold text-dg-fg-primary dark:text-slate-50' : ''}`}
                >
                  {t(item.labelKey)}
                </span>
              </button>
            );
          })}
        </nav>
        <div className="flex shrink-0 flex-col gap-1 border-t border-dg-border-subtle p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] dark:border-slate-800">
          <button
            type="button"
            disabled={!isSignedIn}
            onClick={() => isSignedIn && go(AppPhase.OPTIONS)}
            className="flex min-h-[44px] items-center gap-3 rounded-dg-control px-3 py-2 text-dg-fg-secondary transition-colors hover:bg-dg-interaction-hover disabled:pointer-events-none disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-800/60"
          >
            <HelpCircle className="h-5 w-5 shrink-0" aria-hidden />
            <span className="text-dg-body font-medium">{t('shell.help')}</span>
          </button>
          <button
            type="button"
            disabled
            className="flex min-h-[44px] cursor-not-allowed items-center gap-3 rounded-dg-control px-3 py-2 text-dg-fg-disabled opacity-70 dark:text-slate-600"
            title={t('shell.notificationsSoon')}
          >
            <Bell className="h-5 w-5 shrink-0" aria-hidden />
            <span className="text-dg-body font-medium">{t('shell.notifications')}</span>
          </button>
          <button
            type="button"
            disabled={!isSignedIn}
            onClick={() => isSignedIn && go(AppPhase.OPTIONS)}
            className="flex min-h-[44px] items-center gap-3 rounded-dg-control px-3 py-2 text-dg-fg-secondary transition-colors hover:bg-dg-interaction-hover disabled:pointer-events-none disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-800/60"
          >
            <User className="h-5 w-5 shrink-0" aria-hidden />
            <span className="text-dg-body font-medium">{t('shell.profile')}</span>
          </button>
        </div>
      </aside>
    </div>
  );
}
